/**
 * La cabecera de la home NUEVA (grupo C): tres cards por DESTINO —"Redes sociales",
 * "Catálogos" y "Anuncios"— con los logos de sus plataformas en abanico
 * (ADR-2026-09-29-la-home-se-parte-por-destino, supersede las dos cards de vertiente
 * "Crear imágenes" / "Crear videos"). No navegan: abren un popup con los modos de esa
 * sección, así volver atrás es cerrar un diálogo y no rearmar el layout
 * (ADR-2026-08-31-version2-novedades-arriba-y-popup-por-vertiente). El Agente no vive
 * acá: ni la barra que lo presentaba ni el hero de los rubros sin catálogo propio.
 *
 * Vivía adentro de `HomeMinimal`; salió a su archivo cuando las dos cabeceras pasaron a
 * convivir (ADR-2026-10-07-la-home-elige-su-cabecera): ésta para el grupo C,
 * `CabeceraPorVertiente` para el resto. `HomeMinimal` pone el saludo, los accesos, los
 * banners y todo lo de abajo; acá va sólo "Elegí qué querés crear".
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, X } from 'lucide-react';
import type { ModoHome } from './EstiloMinimal';
// La grilla de modos del popup (la misma que dibuja la solapa "Herramientas" del hero
// del Agente en el teléfono).
import { GrillaDeModos } from '../v2/components/GrillaDeModos';
import { portadaDeRubro } from '../v2/modesCatalog';
import { CARATULAS_CON_TINTE, MODOS_DE_CATALOGO, MODOS_DE_REDES, PROXIMOS_DE_CATALOGO } from './seccionesDeModos';
import { CardModoProximo } from './CardModoProximo';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

type Seccion = 'redes' | 'catalogos' | 'anuncios';

/*
 * Qué modo va en "Catálogos" y en "Redes sociales" sale de `seccionesDeModos`, la MISMA
 * lista que usan los paneles de la sidebar (ADR-2026-10-05-la-sidebar-por-secciones). Antes
 * vivía acá (ADR-2026-09-29-la-home-se-parte-por-destino) y la sidebar no la veía.
 */

/**
 * Modos que la home NO muestra en ninguna sección, aunque existan. Hoy ninguno: Catálogo
 * (y su variante de muebles) estuvo oculto y volvió al popup de Catálogos con otro nombre,
 * "Generación en lote" (ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido). La lista
 * queda para la próxima vez que haga falta esconder uno.
 */
const MODOS_OCULTOS_EN_LA_HOME = new Set<string>();


/** Una plataforma del abanico de logos: archivo en `public/images/logos_plataformas/`. */
interface Plataforma {
  nombre: string;
  logo: string;
  /**
   * Zoom del logo dentro de su cuadrado. Instagram trae sus propias esquinas redondeadas
   * sobre fondo blanco: sin agrandarlo un poco, las puntas blancas asoman por el recorte.
   */
  zoom?: number;
}

/**
 * Copy, tinte y plataformas de las tres cards. El orden de las plataformas es el del
 * abanico: la primera queda ATRÁS y la última al frente.
 */
const SECCIONES: Record<
  Seccion,
  {
    titulo: string;
    bajada: string;
    tituloPopup: string;
    /** Fondo de la card. Los tres tintes tienen la misma luminosidad: ninguno se adelanta. */
    tinte: string;
    plataformas: Plataforma[];
    /** Todavía no tiene modos: la card se ve pero no abre nada. */
    proximamente?: boolean;
    /**
     * La card NAVEGA a una pantalla en vez de abrir el popup de modos. Hoy ninguna la usa:
     * Redes sociales iba derecho al modo Redes y ahora da a elegir entre crear y copiar
     * (ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido).
     */
    ruta?: string;
    /**
     * Modos que todavía no existen y se anuncian en el popup, al final de la grilla,
     * como cards de "Próximamente" que no se abren.
     */
    modosProximos?: { nombre: string; bajada: string }[];
    /**
     * Las carátulas del popup con el fondo del tinte de la sección en vez del lavanda de
     * siempre (ADR-2026-10-05-caratulas-con-el-tinte-de-su-seccion). Son las mismas
     * carátulas recoloreadas con `scripts/caratulas-con-tinte/recolorear.py`. Una portada
     * del rubro gana: es otra foto, no la misma con otro fondo.
     */
    caratulas?: Readonly<Record<string, string>>;
  }
> = {
  redes: {
    titulo: 'Redes sociales',
    bajada: 'Posteos, carruseles y videos listos para publicar.',
    tituloPopup: '¿Cómo querés arrancar?',
    tinte: 'bg-[#F2ECF5]',
    plataformas: [
      { nombre: 'Facebook', logo: '/images/logos_plataformas/facebook.png' },
      { nombre: 'TikTok', logo: '/images/logos_plataformas/tiktok.png' },
      { nombre: 'Instagram', logo: '/images/logos_plataformas/instagram.png', zoom: 1.1 },
    ],
    caratulas: CARATULAS_CON_TINTE.redes,
  },
  catalogos: {
    titulo: 'Catálogos',
    get bajada() {
      return `Fotos de producto y con ${palabraDeModeloActual().plural} para tu tienda.`;
    },
    tituloPopup: '¿Qué querés crear para tu catálogo?',
    tinte: 'bg-[#F4F1E6]',
    plataformas: [
      { nombre: 'Shopify', logo: '/images/logos_plataformas/shopify.png' },
      { nombre: 'Tiendanube', logo: '/images/logos_plataformas/tiendanube.png' },
      { nombre: 'Mercado Libre', logo: '/images/logos_plataformas/mercadolibre.png' },
    ],
    modosProximos: [...PROXIMOS_DE_CATALOGO],
    caratulas: CARATULAS_CON_TINTE.catalogo,
  },
  anuncios: {
    titulo: 'Anuncios',
    bajada: 'Piezas para tus campañas en Meta y Google.',
    tituloPopup: '¿Qué anuncio querés crear?',
    tinte: 'bg-[#EDEFF5]',
    plataformas: [
      { nombre: 'Google Ads', logo: '/images/logos_plataformas/google-ads.png' },
      { nombre: 'Meta', logo: '/images/logos_plataformas/meta.png' },
    ],
    proximamente: true,
  },
};

/**
 * Dónde cae cada cuadrado del abanico, del de ATRÁS al del frente. No van uno al lado
 * del otro: se pisan y cada uno gira para su lado, como fichas apoyadas sobre la card.
 * `hover` es cómo quedan al pasar el mouse: se abren un poco, para que la card
 * responda sin moverse de lugar.
 */
const POSICIONES_DEL_ABANICO: Record<
  2 | 3,
  { reposo: string; hover: string }[]
> = {
  2: [
    { reposo: 'translate(-40px, 8px) rotate(-10deg)', hover: 'translate(-50px, 6px) rotate(-14deg)' },
    { reposo: 'translate(36px, -6px) rotate(7deg)', hover: 'translate(46px, -10px) rotate(10deg)' },
  ],
  3: [
    { reposo: 'translate(-70px, 12px) rotate(-12deg)', hover: 'translate(-82px, 12px) rotate(-16deg)' },
    { reposo: 'translate(0px, -8px) rotate(4deg)', hover: 'translate(0px, -14px) rotate(6deg)' },
    { reposo: 'translate(70px, 10px) rotate(-5deg)', hover: 'translate(82px, 10px) rotate(-2deg)' },
  ],
};

/**
 * El abanico de logos de una card: cuadrados de esquinas redondeadas, girados y
 * superpuestos. Dice DÓNDE va a terminar lo que se crea, que es lo que distingue a
 * las tres cards; los modos quedan para el popup.
 *
 * `group-hover` y no estado propio: el hover es de la card entera, no del abanico.
 */
function AbanicoDePlataformas({ plataformas }: { plataformas: Plataforma[] }) {
  const lista = plataformas.slice(0, 3);
  const posiciones = POSICIONES_DEL_ABANICO[lista.length === 3 ? 3 : 2];
  return (
    <span aria-hidden="true" className="relative block h-[112px] w-[260px]">
      {lista.map((p, i) => (
        <span
          key={p.nombre}
          className="absolute left-1/2 top-1/2 -ml-[42px] -mt-[42px] h-[84px] w-[84px] transition-transform duration-500 ease-out [transform:var(--reposo)] group-hover:[transform:var(--hover)]"
          style={
            {
              '--reposo': posiciones[i].reposo,
              '--hover': posiciones[i].hover,
            } as CSSProperties
          }
        >
          {/* El marco va en un span aparte del que gira: el borde blanco y la sombra
              son de la ficha, y así el `overflow-hidden` recorta el logo y no la sombra. */}
          <span className="block h-full w-full overflow-hidden rounded-[22px] bg-white shadow-[0_10px_28px_rgba(16,24,45,0.18)] ring-[3px] ring-white">
            <img
              src={p.logo}
              alt=""
              className="h-full w-full object-cover"
              style={p.zoom ? { transform: `scale(${p.zoom})` } : undefined}
            />
          </span>
        </span>
      ))}
    </span>
  );
}

/**
 * Card de SECCIÓN — la que abre el popup con los modos de ese destino
 * (ADR-2026-09-29-la-home-se-parte-por-destino, supersede las cards "Crear imágenes" /
 * "Crear videos"). La pregunta ya no es "¿imagen o video?" sino "¿para dónde es?".
 *
 * Las tres van CLARAS y con la misma luminosidad, como las vertientes de antes: lo que
 * las distingue es el tinte y los logos, no el peso. La de "Anuncios" todavía no tiene
 * modos: se dibuja igual —anuncia lo que viene—, no es clickeable y lleva la pastilla
 * "Próximamente" donde las otras tienen la flecha.
 */
function CardSeccion({
  seccion,
  cantidad,
  onElegir,
}: {
  seccion: Seccion;
  cantidad: number;
  onElegir: () => void;
}) {
  const copy = SECCIONES[seccion];
  const proximamente = copy.proximamente || cantidad === 0;
  return (
    <button
      type="button"
      onClick={proximamente ? undefined : onElegir}
      disabled={proximamente}
      // `aria-haspopup`: el botón no lleva a otra pantalla, abre un diálogo.
      aria-haspopup={proximamente ? undefined : 'dialog'}
      className={`group relative flex h-[268px] flex-col lg:aspect-square lg:h-auto overflow-hidden rounded-2xl border border-black/[0.06] p-6 text-left transition-shadow disabled:cursor-default ${copy.tinte} ${
        proximamente ? '' : 'hover:shadow-[0_10px_36px_rgba(16,24,45,0.12)]'
      }`}
    >
      <span className="flex flex-1 items-center justify-center">
        <AbanicoDePlataformas plataformas={copy.plataformas} />
      </span>

      <span className="flex items-end gap-4">
        <span className="min-w-0 flex-1">
          {/* Sin etiqueta arriba del título (ni la cantidad de herramientas): la card
              dice el destino y nada más. "Próximamente" va abajo a la derecha, en el
              lugar de la flecha. */}
          <span className="block text-[22px] font-bold leading-tight tracking-[-0.02em] text-brand-black/90">
            {copy.titulo}
          </span>
          <span className="mt-1 block text-[13.5px] leading-snug text-black/50">
            {copy.bajada}
          </span>
        </span>
        {proximamente ? (
          // La misma pastilla que `CardModoProximo` en el popup
          // (ADR-2026-10-07-anuncios-dice-proximamente).
          <span className="inline-flex shrink-0 items-center rounded-full bg-white/90 px-3 py-1.5 text-[12px] font-bold text-brand-black shadow-lg backdrop-blur">
            Próximamente
          </span>
        ) : (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-primary text-white transition-transform duration-300 group-hover:translate-x-1">
            <ArrowRight className="h-[18px] w-[18px]" />
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * El popup con los modos de una sección.
 *
 * Va por `createPortal` a `document.body` y no en su lugar del árbol: el shell envuelve
 * el contenido en contenedores con `transform` para el fade entre secciones, y un
 * `position: fixed` dentro de un ancestro transformado se posiciona contra ESE ancestro
 * en vez de contra el viewport — el overlay habría quedado corrido. Es el mismo motivo
 * por el que el menú de cuenta del shell también se portalea.
 */
function PopupPorSeccion({
  seccion,
  modos,
  rubroMarca,
  onAbrir,
  onCerrar,
}: {
  seccion: Seccion;
  modos: ModoHome[];
  rubroMarca: string | null;
  onAbrir: (m: ModoHome) => void;
  onCerrar: () => void;
}) {
  // Escape cierra. Se escucha en `document` y no en el diálogo para que funcione sin
  // depender de dónde quedó el foco al abrir.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  // Cuántas cards dibuja la grilla (los modos y los "Próximamente"): con una, dos o
  // cuatro, dos columnas y un diálogo más angosto — cuatro en tres columnas dejaban una
  // fila de tres y una card sola abajo (Catálogos), y en 2×2 cierra pareja
  // (ADR-2026-10-05-popup-de-catalogos-en-2x2).
  const cantidad = modos.length + (SECCIONES[seccion].modosProximos?.length ?? 0);
  const pocas = cantidad <= 2 || cantidad === 4;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={SECCIONES[seccion].tituloPopup}
    >
      {/* El fondo cierra al click. Es un `button` y no un `div` con `onClick` para que
          ese cierre también esté al alcance del teclado. */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]"
      />
      {/* `max-h-[85vh]` + scroll propio: con 7 modos la grilla mide tres filas y en
          una pantalla de 800px de alto el diálogo se salía por abajo. */}
      <div
        className={`animate-fade-in relative z-10 max-h-[85vh] w-full overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_24px_80px_rgba(16,24,45,0.28)] ${
          // Con una, dos o cuatro cards el diálogo se angosta a dos columnas: en tres
          // quedaba un hueco blanco donde iría la tercera (Redes sociales tiene dos).
          pocas ? 'max-w-[590px]' : 'max-w-[860px]'
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">
              {SECCIONES[seccion].tituloPopup}
            </h2>
            <p className="mt-1 text-[13.5px] text-black/50">
              Elegí la herramienta con la que querés empezar.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-black/50 transition-colors hover:bg-black/[0.05] hover:text-black/80"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <GrillaDeModos
          // Esta home es la interfaz nueva: "Recrear", no "Plantillas"
          // (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva).
          uiNueva
          modos={modos}
          rubroMarca={rubroMarca}
          onAbrir={onAbrir}
          className={`mt-5 grid grid-cols-2 gap-3 ${pocas ? '' : 'sm:grid-cols-3'}`}
          caratulaPropia={(path) =>
            portadaDeRubro(path, rubroMarca) ? undefined : SECCIONES[seccion].caratulas?.[path]
          }
          alFinal={SECCIONES[seccion].modosProximos?.map((p) => (
            <CardModoProximo key={p.nombre} nombre={p.nombre} bajada={p.bajada} />
          ))}
        />
      </div>
    </div>,
    document.body,
  );
}

export function CabeceraPorDestino({
  modos,
  rubroMarca,
  abrirModo,
  irA,
}: {
  modos: ModoHome[];
  rubroMarca: string | null;
  /** Abrir un modo (candado → ejemplos o planes; si no, el modo con el fade del shell). */
  abrirModo: (m: ModoHome) => void;
  /** Navegar a una ruta con el fade del shell (para una sección con `ruta`). */
  irA: (ruta: string) => void;
}) {
  /** Qué popup está abierto. `null` = ninguno, que es como abre la pantalla. */
  const [popup, setPopup] = useState<Seccion | null>(null);

  const porSeccion = useMemo<Record<Seccion, ModoHome[]>>(() => {
    const visibles = modos.filter((m) => !MODOS_OCULTOS_EN_LA_HOME.has(m.path));
    // En el orden de la lista compartida, una vez cada uno.
    const enOrden = (paths: readonly string[]) =>
      paths.flatMap((path) => {
        const m = visibles.find((x) => x.path === path);
        return m ? [m] : [];
      });
    const catalogos = enOrden(MODOS_DE_CATALOGO);
    // Crear y Recrear, en ese orden y una vez cada uno (Plantillas
    // llega dos veces, una por familia: se toma la primera, la de imagen). Un rubro sin
    // plantillas se queda con una sola opción y la card abre el modo derecho.
    const redes = MODOS_DE_REDES.flatMap((path) => {
      const m = visibles.find((x) => x.path === path);
      return m ? [m] : [];
    });
    // "Anuncios" todavía no tiene modos: la card sale como "Próximamente".
    return { redes, catalogos, anuncios: [] };
  }, [modos]);

  // El popup se cierra ANTES de navegar: el fade de salida del shell apaga el
  // contenido de la home, y el overlay —que cuelga de `body`— no se enteraría.
  const abrir = (m: ModoHome) => {
    setPopup(null);
    abrirModo(m);
  };

  return (
    <>
      {/* ── Secciones ───────────────────────────────────────────────────────
          Tres cards por DESTINO —redes, catálogos, anuncios— que abren el popup con
          los modos de esa sección (ADR-2026-09-29-la-home-se-parte-por-destino). El
          Agente ya no vive en la home de escritorio: ni la barra que lo presentaba ni
          el hero que la reemplazaba en los rubros sin catálogo propio. */}
      <section className="mt-8">
        <h2 className="text-[15px] font-semibold text-black/45">
          Elegí qué querés crear
        </h2>
        {/* Tres columnas desde `lg`: más angosto, con la sidebar al lado, cada card
            quedaba de ~200px y el abanico de logos no entraba. Desde `lg` las cards son
            CUADRADAS (`aspect-square` en `CardSeccion`); apiladas en una columna, un
            cuadrado del ancho entero sería enorme, así que ahí quedan en 268px. */}
        <div className="mt-3 grid grid-cols-1 gap-2.5 lg:grid-cols-3">
          {(['redes', 'catalogos', 'anuncios'] as const).map((s) =>
            porSeccion[s].length === 0 && !SECCIONES[s].proximamente && !SECCIONES[s].ruta ? null : (
              <CardSeccion
                key={s}
                seccion={s}
                cantidad={porSeccion[s].length}
                onElegir={() => {
                  const ruta = SECCIONES[s].ruta;
                  if (ruta) irA(ruta);
                  // Con una sola opción no hay nada que elegir: se abre directo.
                  else if (porSeccion[s].length === 1 && !SECCIONES[s].modosProximos?.length) abrir(porSeccion[s][0]);
                  else setPopup(s);
                }}
              />
            ),
          )}
        </div>
      </section>

      {popup && (
        <PopupPorSeccion
          seccion={popup}
          modos={porSeccion[popup]}
          rubroMarca={rubroMarca}
          onAbrir={abrir}
          onCerrar={() => setPopup(null)}
        />
      )}
    </>
  );
}
